/** Normalized StatCard component for dashboard metrics */
import { useEffect } from 'react';
import { ArrowUpRight, ArrowDownRight, type LucideIcon } from 'lucide-react';

import { motion, useSpring, useTransform } from 'framer-motion';

interface MetricCardProps {
  title: string;
  value: number;
  change: string | null;
  changeType: 'up' | 'down';
  icon: LucideIcon;
  formatter?: (val: number) => string;
  onClick?: () => void;
  changeLabel?: string;
}

export function AnimatedNumber({ value, formatter }: { value: number; formatter?: (v: number) => string }) {
  const spring = useSpring(0, { mass: 1, stiffness: 60, damping: 20 });
  
  // Use Math.floor to ensure whole numbers during the count-up animation
  const display = useTransform(spring, (current) => {
    const val = Math.floor(current);
    return formatter ? formatter(val) : val.toLocaleString();
  });

  useEffect(() => {
    spring.set(value ?? 0);
  }, [value, spring]);

  return <motion.span>{display}</motion.span>;
}

export function MetricCard({
  title, value, change, changeType, icon: Icon, formatter, onClick, changeLabel = 'vs last month'
}: MetricCardProps) {
  return (
    <div 
      onClick={onClick}
      className={`liquid-glass rounded-2xl p-6 hover-lift hover-glow transition-all duration-200 border border-[var(--t-border-subtle)] ${onClick ? 'cursor-pointer active:scale-[0.98]' : ''}`}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm text-[var(--t-text-secondary)] font-medium">{title}</p>
          <div className="text-3xl font-black mt-1 italic tracking-tighter" style={{ color: 'var(--t-text)' }}>
            <AnimatedNumber value={value ?? 0} formatter={formatter} />
          </div>
        </div>
        <div className={`p-2.5 rounded-xl`} style={{ 
          background: 'var(--t-accent-gradient)',
          color: 'var(--t-on-accent, #ffffff)',
          opacity: 0.9
        }}>
          <Icon size={20} />
        </div>
      </div>
      <div className="flex items-center gap-1.5 mt-3">
        {change ? (
          <>
            {changeType === 'up' ? (
              <ArrowUpRight size={14} className="text-[var(--t-success)]" />
            ) : (
              <ArrowDownRight size={14} className="text-[var(--t-error)]" />
            )}
            <span className={`text-[10px] font-black uppercase tracking-widest ${changeType === 'up' ? 'text-[var(--t-success)]' : 'text-[var(--t-error)]'}`}>
              {change}
            </span>
            <span className="text-[10px] text-[#6d758c] font-bold uppercase tracking-widest ml-1">{changeLabel}</span>
          </>
        ) : (
          <span className="text-[10px] text-[#6d758c] font-bold uppercase tracking-widest">N/A {changeLabel}</span>
        )}
      </div>
    </div>
  );
}
